// useShelterAddresses
// -------------------
// Custom React hook — fetches the registered delivery addresses for a shelter
// and exposes them as formatted strings (for the non-registered warning), raw
// DB rows (for DeliverySelect radio-cards), and a loaded flag.
// The fetch re-runs whenever the request changes (requestId dependency).
// TypeScript source — useShelterAddresses.js is a thin re-export stub.

import React, { useState } from "react";
import { type DeliveryAddressRow, listActiveByShelterId } from "../data/deliveryAddresses";

export function useShelterAddresses(
  requestId: number | string | null | undefined,
  shelterId: string | null | undefined,
  // Deprecated/unused: delivery_addresses has no shelter_name column, so it can't
  // be filtered by name. Kept positionally for the existing call site. Addresses
  // are keyed by shelter_id.
  _shelterName?: string | null | undefined
) {
  var [shelterRegAddrs, setShelterRegAddrs] = useState<string[]>([]); // string[] for comparison
  var [shelterRegAddrObjs, setShelterRegAddrObjs] = useState<DeliveryAddressRow[]>([]); // raw DB rows
  var [shelterAddrsLoaded, setShelterAddrsLoaded] = useState(false); // did the load complete?

  React.useEffect(
    function () {
      if (!requestId) return;
      // Registered addresses are keyed by shelter_code (the repo bakes in the
      // WHERE NOT deactivated rule). Without a shelter_code there's nothing to look
      // up, so mark the load complete with no registered rows.
      if (!shelterId) {
        setShelterAddrsLoaded(true);
        return;
      }
      listActiveByShelterId(shelterId).then(function (rows) {
        setShelterAddrsLoaded(true);
        // db-147: never offer a BANNED address in the responder's Edit-Address
        // picker — re-routing must land on a usable address. (Unverified + silent
        // rows are kept: staff are the trusted party and may deliberately pick them.)
        const usable = (rows || []).filter(function (a) {
          return a.banned_address !== true;
        });
        if (usable.length > 0) {
          setShelterRegAddrObjs(usable);
          // Format matches fmtAddr (no address_name) — same as what gets saved.
          setShelterRegAddrs(
            usable.map(function (a) {
              return [a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
                .filter(Boolean)
                .join(", ");
            })
          );
        }
      });
    },
    [requestId]
  );

  return { shelterRegAddrs, shelterRegAddrObjs, shelterAddrsLoaded };
}
